import type { ConsoleUsage } from '@plato/shared';
import { useCallback, useRef, useState } from 'react';
import { DataTable } from '../../components/data-table.tsx';
import { formatCount } from '../../components/format.ts';
import { formatCredits } from './format.ts';

type Day = ConsoleUsage['by_day'][number];

const HEIGHT = 220;
const MARGIN = { top: 12, right: 8, bottom: 26, left: 48 };
const MAX_BAR = 24;
const GAP = 2;
const RADIUS = 4;

export function DailyCreditsChart(props: { days: readonly Day[] }) {
  const { days } = props;
  const [ref, width] = useWidth<HTMLDivElement>(720);
  const [active, setActive] = useState<number | null>(null);
  const max = Math.max(0, ...days.map((day) => day.credit_micros));
  if (days.length === 0 || max === 0) {
    return <p className="muted">No credits were used in this range.</p>;
  }

  const ticks = niceTicks(max);
  const top = ticks.at(-1) ?? max;
  const plotWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const band = plotWidth / days.length;
  const barWidth = Math.max(1, Math.min(MAX_BAR, band - GAP));
  const y = (value: number) => MARGIN.top + plotHeight - (value / top) * plotHeight;
  const labelEvery = Math.ceil(days.length / 6);
  const activeDay = active === null ? undefined : days[active];

  return (
    <figure className="chart">
      <div className="chart-plot" ref={ref}>
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={`Credits per day, ${days[0]?.day} to ${days.at(-1)?.day}`}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                className="chart-grid"
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={y(tick)}
                y2={y(tick)}
              />
              <text
                className="chart-tick"
                x={MARGIN.left - 6}
                y={y(tick)}
                dy="0.32em"
                textAnchor="end"
              >
                {formatCredits(tick)}
              </text>
            </g>
          ))}
          {days.map((day, index) => {
            const x = MARGIN.left + index * band;
            const showLabel =
              index === 0 ||
              index === days.length - 1 ||
              (index % labelEvery === 0 && days.length - 1 - index >= labelEvery / 2);
            return (
              <g key={day.day}>
                <path
                  className={index === active ? 'chart-bar active' : 'chart-bar'}
                  d={columnPath(x + (band - barWidth) / 2, y(day.credit_micros), barWidth, y(0))}
                />
                {showLabel && (
                  <text className="chart-tick" x={x + band / 2} y={HEIGHT - 8} textAnchor="middle">
                    {shortDay(day.day)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {days.map((day, index) => (
          <button
            key={day.day}
            type="button"
            className="chart-hit"
            style={{
              left: MARGIN.left + index * band,
              top: MARGIN.top,
              width: band,
              height: plotHeight,
            }}
            aria-label={`${shortDay(day.day)}: ${formatCredits(day.credit_micros)} credits`}
            onPointerEnter={() => setActive(index)}
            onPointerLeave={() => setActive(null)}
            onFocus={() => setActive(index)}
            onBlur={() => setActive(null)}
          />
        ))}
        {activeDay !== undefined && active !== null && (
          <div
            className="chart-tooltip"
            role="status"
            style={{
              left: Math.min(MARGIN.left + (active + 0.5) * band, width - 120),
              top: Math.max(0, y(activeDay.credit_micros) - 56),
            }}
          >
            <strong>{formatCredits(activeDay.credit_micros)} credits</strong>
            <span>
              {shortDay(activeDay.day)} · {formatCount(activeDay.runs)} replies
            </span>
          </div>
        )}
      </div>
      <details>
        <summary>Show as table</summary>
        <DataTable
          caption="Credits per day"
          columns={[
            { header: 'Day (UTC)', cell: (row) => row.day },
            { header: 'Credits', cell: (row) => formatCredits(row.credit_micros) },
            { header: 'Replies', cell: (row) => formatCount(row.runs) },
          ]}
          rows={days.map((day) => ({ ...day, id: day.day }))}
        />
      </details>
    </figure>
  );
}

function columnPath(x: number, top: number, width: number, baseline: number): string {
  const height = baseline - top;
  if (height <= 0) return '';
  const r = Math.min(RADIUS, width / 2, height);
  return [
    `M${x},${baseline}`,
    `V${top + r}`,
    `Q${x},${top} ${x + r},${top}`,
    `H${x + width - r}`,
    `Q${x + width},${top} ${x + width},${top + r}`,
    `V${baseline}`,
    'Z',
  ].join(' ');
}

export function niceTicks(max: number): number[] {
  const rough = max / 4;
  const power = 10 ** Math.floor(Math.log10(rough));
  const step = ([1, 2, 5, 10].find((m) => m * power >= rough) ?? 10) * power;
  const ticks: number[] = [];
  for (let tick = 0; tick < max + step; tick += step) ticks.push(tick);
  return ticks;
}

function shortDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function useWidth<E extends HTMLElement>(fallback: number) {
  const [width, setWidth] = useState(fallback);
  const observer = useRef<ResizeObserver | null>(null);
  const ref = useCallback((element: E | null) => {
    observer.current?.disconnect();
    if (element === null || typeof ResizeObserver === 'undefined') return;
    observer.current = new ResizeObserver(([entry]) => {
      if (entry !== undefined && entry.contentRect.width > 0) setWidth(entry.contentRect.width);
    });
    observer.current.observe(element);
  }, []);
  return [ref, width] as const;
}
